import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import { ServiceBusManagementClient } from "@azure/arm-servicebus";
import { FilterList, Clear, Email, ArrowDropDown, ArrowRight, HourglassFull } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography, TextField, Grid, IconButton, InputAdornment, CircularProgress, Alert, Link } from '@mui/material';
import AzureIcon from '../icons/azure';
import NamespaceIcon from '../icons/namespace';
import { blue, purple, deepPurple } from '@mui/material/colors';
import { SubscriptionClient } from '@azure/arm-resources-subscriptions';

const Root = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(0.5),
  paddingTop: "0px",
}));

const Spinner = styled(CircularProgress)(({ theme }) => ({
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: theme.spacing(3),
}));

const FilterContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingRight: theme.spacing(4), //room for close explorer button
}));

const ClearFilterIcon = styled(IconButton)({
  padding: 2,
});

const AlertContainer = styled(Alert)(({ theme }) => ({
  margin: theme.spacing(3),
}));

const StyledTreeItem = styled((props) => (
  <TreeItem {...props} />
))(({ theme, depth = 1 }) => ({
  [`& .${treeItemClasses.root}`]: {
    color: theme.palette.text.secondary,
    '&:hover > .MuiTreeItem-content': {
      backgroundColor: theme.palette.action.hover,
      paddingTop:0,
      paddingBottom:0
    },
    '&:focus > .MuiTreeItem-content, &.Mui-selected > .MuiTreeItem-content': {
      backgroundColor: theme.palette.grey[250],
      color: 'var(--tree-view-color)',
      paddingTop:0,
      paddingBottom:0

    },
    '&:focus > .MuiTreeItem-content .MuiTreeItem-label, &:hover > .MuiTreeItem-content .MuiTreeItem-label, &.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
      backgroundColor: 'transparent',
      paddingTop:0,
      paddingBottom:0

    },
    '&.Mui-selected > .MuiTreeItem-content': {
      backgroundColor: theme.palette.grey[300],
      paddingTop:0,
      paddingBottom:0

    }
  },
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingTop:0,
    paddingBottom:0,
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '&.Mui-expanded': {
      fontWeight: theme.typography.fontWeightRegular,
    },

  },
  [`& .${treeItemClasses.group}`]: {
    paddingTop:0,
    paddingBottom:0,
    marginLeft: 0,

    [`& .${treeItemClasses.content}`]: {
      paddingLeft: `calc(var(--node-depth) * ${theme.spacing(1.5)})`,
    },
  },
  '--tree-view-color': props => props.color || 'inherit',
  '--tree-view-bg-color': props => props.bgColor || 'transparent',
  '--node-depth': depth,
}));

const TreeItemLabelRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  verticalAlign: 'top',
  alignItems: 'center',
  padding: theme.spacing(0.5, 0),
  paddingTop: theme.spacing(0.3),
  paddingBottom: theme.spacing(0.3),
}));

const TreeItemLabelIcon = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const TreeItemLabelText = styled(Typography)({
  fontWeight: 'inherit',
  flexGrow: 1,
  wordBreak: 'break-all',
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const TreeItemLabelInfo = styled(Typography)({
  paddingLeft: '3px',
  whiteSpace: "nowrap",
  textAlign: 'right',
});

function CustomTreeItem(props) {
  const { labelText, labelIcon: LabelIcon, labelIconColor = "inherit", labelIconStyle, labelInfo, color, bgColor, depth = 1, labelTooltip = "", nodeId, onLabelClick, onClick, ...other } = props;
  return (
    <StyledTreeItem
      color={color}
      bgcolor={bgColor}
      depth={depth}
      itemId={nodeId}
      onClick={(event) => { 
        event.stopPropagation();
        event.preventDefault();
        if(onClick) onClick(); 
      }}
      label={
          <TreeItemLabelRoot onClick={onLabelClick}>
            <TreeItemLabelIcon>
              <LabelIcon fontSize="small" color={labelIconColor} style={labelIconStyle} />
            </TreeItemLabelIcon>
            <Tooltip title={labelTooltip} placement="top-start" enterDelay={600} enterNextDelay={600} leaveDelay={1} enterTouchDelay={500} leaveTouchDelay={1}>
              <TreeItemLabelText variant="body2">
                {labelText}
              </TreeItemLabelText>
            </Tooltip>
            {
              labelInfo && labelInfo.length > 0 ?
              <TreeItemLabelInfo variant="caption" color="inherit">
                {labelInfo}
              </TreeItemLabelInfo> : null
            }
          </TreeItemLabelRoot>
      }
      {...other}
    />
  );
}

CustomTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelIcon: PropTypes.elementType.isRequired,
  labelInfo: PropTypes.string,
  labelText: PropTypes.string.isRequired,
  depth: PropTypes.number,
  labelTooltip: PropTypes.string,
  labelIconColor: PropTypes.string,
  labelIconStyle: PropTypes.object,
  nodeId: PropTypes.string.isRequired,
};

// sort by entity name
const itemSort = (a, b) => {
  const nameA = a.name.toUpperCase();
  const nameB = b.name.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
}


export default function EntityBrowser(props) {
  const { credentials, onEntitySelected, setErrorMessage, selectedEntity, onEntityAdditionalInfoProvided } = props;
  const [subscriptionsWithNamespaces, setSubscriptionsWithNamespaces] = useState(undefined);
  const [filter, setFilter] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState();
  const [subscriptions, setSubscriptions] = useState([]);

  const selectedNodeIdRef = useRef();
  selectedNodeIdRef.current = selectedNodeId;

  const subscriptionsWithNamespacesRef = useRef();
  subscriptionsWithNamespacesRef.current = subscriptionsWithNamespaces;

  // filter by entity name filter text
  const entityNameFilter = ({ name }) => { 
    if (filter.length <= 0) return true
    return name.indexOf(filter) > -1;
  };

  // auto update selected entity based on state changes (i.e. new message counts after refresh) if applicable
  useEffect(() => {
    let update = {};
    if(selectedEntity)
    {
      const { type, namespaceId, subscriptionId, id } = selectedEntity;
      switch (type) {
        case "topic":
          update = subscriptionsWithNamespaces[subscriptionId].namespaces[namespaceId].topics?  {
            countDetails: subscriptionsWithNamespaces[subscriptionId].namespaces[namespaceId].topics[id].countDetails
          } : {};
        break;
        case "queue":
          update = subscriptionsWithNamespaces[subscriptionId].namespaces[namespaceId].queues? {
            countDetails: subscriptionsWithNamespaces[subscriptionId].namespaces[namespaceId].queues[id].countDetails
          } : {};
        break;
        case "subscription":
          const { topic } = selectedEntity;
          update = subscriptionsWithNamespaces[subscriptionId].namespaces[namespaceId].topics? {
            countDetails: subscriptionsWithNamespaces[subscriptionId].namespaces[namespaceId].topics[topic.id].subscriptions.find((subscription) => subscription.id === id).countDetails,
          }: {};
        break;
        default:
          break;
      }
      onEntityAdditionalInfoProvided(update);
    }
  // react-hooks/exhaustive-deps is disabled as this is one-way update flow
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [subscriptionsWithNamespaces]);

  useEffect(() =>{
    const doAsync = async () => {
      try {
        const subscriptionClient = new SubscriptionClient(credentials);
        const subscriptions = subscriptionClient.subscriptions.list();
        const subscriptionArray = [];
        for await (const subscription of subscriptions){
            subscriptionArray.push(subscription);
        }
        setSubscriptions(subscriptionArray.filter(sub => (sub.displayName !== "Access to Azure Active Directory")));
      } catch (error) {
        setErrorMessage("Error loading subscriptions, " + error.toString() );
        throw error;
      }
    }
    doAsync();
  }, [credentials, setErrorMessage])

  useEffect(() => {
    const doAsync = async () => {
      if (credentials && subscriptions)
      {
        try {
          await Promise.all(subscriptions.map(async (subscription) => {
            const context = new ServiceBusManagementClient(credentials, subscription.subscriptionId)
            const namespacesIterator = context.namespaces.list();
            const namespaces = [];
            for await (const namespace of namespacesIterator) {
              namespaces.push(namespace);
            }
            if(namespaces.length > 0)
            {
              const item = {
                id: subscription.subscriptionId,
                name: subscription.displayName,
                namespaces: namespaces.reduce((namespaces, namespace) => {
                  return Object.assign(namespaces, {
                    [namespace.id]: {
                      subscriptionId: subscription.subscriptionId,
                      serviceBusEndpoint: namespace.serviceBusEndpoint,
                      context: () => context,
                      ...namespace
                    }
                  })
                }, {}),
              };
              setSubscriptionsWithNamespaces((previous) => ({
                ...(previous || {}),
                [subscription.subscriptionId]: item,
              }));
            }
          }));
        } catch (error) {
          setErrorMessage("Error loading namespaces, " + error.toString() );
          throw error;
        }
      }
    };
    doAsync();
  }, [subscriptions, credentials, setErrorMessage]);


  const setIsNamespaceLoading = (namespace, isLoading) => {
    setSubscriptionsWithNamespaces((previous) => ({
      ...previous,
      [namespace.subscriptionId]: {
        ...previous[namespace.subscriptionId],
        namespaces: { 
          ...previous[namespace.subscriptionId].namespaces,
          [namespace.id]: {
            ...previous[namespace.subscriptionId].namespaces[namespace.id],
            isLoading
          }
        },
      }
    }));
  };


  const onNamespaceSelected = async (namespace) => {
    try {
      const refresh = () => { 
        // call onNamespaceSelected if not namespace already loading
        if(!subscriptionsWithNamespacesRef.current[namespace.subscriptionId].namespaces[namespace.id].isLoading) {
          onNamespaceSelected(namespace);
        }
      };
      setIsNamespaceLoading(namespace, true);
      const [ , , , , resourceGroupName] = namespace.id.split("/");
      const topicsIterator = namespace.context().topics.listByNamespace(resourceGroupName, namespace.name);
      const queuesIterator = namespace.context().queues.listByNamespace(resourceGroupName, namespace.name);
      const loadTopics = async () => {
        for await (const topics of topicsIterator.byPage()) {
          setSubscriptionsWithNamespaces((previous) => ({
            ...previous,
            [namespace.subscriptionId]: {
              ...previous[namespace.subscriptionId],
              namespaces: { 
                ...previous[namespace.subscriptionId].namespaces,
                [namespace.id]: {
                  ...previous[namespace.subscriptionId].namespaces[namespace.id],
                  topics: {
                    ...(previous[namespace.subscriptionId].namespaces[namespace.id].topics || {}),
                    ...(topics.reduce((topics, topic) => Object.assign({}, topics, {
                      ...topics,
                      [topic.id]: {
                        ...((previous[namespace.subscriptionId].namespaces[namespace.id].topics || {})[topic.id] || {}),
                        ...topic,
                        refresh,
                        namespace: namespace
                      }
                    }), {})) 
                  }
                }
              },
            }
          }));
        }
      }
      const loadQueues = async () => {
        for await (const queues of queuesIterator.byPage()) {
          setSubscriptionsWithNamespaces((previous) => ({
            ...previous,
            [namespace.subscriptionId]: {
              ...previous[namespace.subscriptionId],
              namespaces: { 
                ...previous[namespace.subscriptionId].namespaces,
                [namespace.id]: {
                  ...previous[namespace.subscriptionId].namespaces[namespace.id],
                  queues: {
                    ...(previous[namespace.subscriptionId].namespaces[namespace.id].queues || {}),
                    ...(queues.reduce((queues, queue) => Object.assign({}, queues, {
                      ...queues,
                      [queue.id]: {
                        ...((previous[namespace.subscriptionId].namespaces[namespace.id].queues || {})[queue.id] || {}),
                        ...queue,
                        refresh,
                        namespace: namespace
                      }
                    }), {}))
                  }
                }
              },
            }
          }));
        }
      }
      await Promise.all([loadTopics(), loadQueues()]);
      setIsNamespaceLoading(namespace, false);
    } catch (error) {
      setIsNamespaceLoading(namespace, false);
      setErrorMessage("Error selecting namespace or loading queues and topics, " + error.toString() );
      throw error;
    }
  }


  const onTopicSelected = async (topic) => {
    try {
      const { namespace, refresh } = topic;
      const loadSubscriptions = async (topic) => {
        const [ , , , , resourceGroupName] = namespace.id.split("/");
        const subscriptionsIterator = namespace.context().subscriptions.listByTopic(resourceGroupName, namespace.name, topic.name);
        const subscriptions = [];
        for await (const subscription of subscriptionsIterator) {
          subscriptions.push(subscription);
        }
        setSubscriptionsWithNamespaces((previous) => ({
          ...previous,
          [namespace.subscriptionId]: {
            ...previous[namespace.subscriptionId],
            namespaces: { 
              ...previous[namespace.subscriptionId].namespaces,
              [namespace.id]: {
                ...previous[namespace.subscriptionId].namespaces[namespace.id],
                topics: {
                  ...previous[namespace.subscriptionId].namespaces[namespace.id].topics,
                  [topic.id]: {
                    ...previous[namespace.subscriptionId].namespaces[namespace.id].topics[topic.id],
                    subscriptions: subscriptions.map((subscription) => Object.assign({}, subscription, {
                      topic: topic,
                      refresh: () => loadSubscriptions(topic)
                    }))
                  }
                }
              }
            }
          }
        }));
      };
      onEntitySelected({
        namespaceName: namespace.name,
        namespaceId: namespace.id,
        namespaceUrl: namespace.serviceBusEndpoint,
        subscriptionId: namespace.subscriptionId,
        ...topic,
        // combine existing topic refresh with a callback to reload subscriptions of that topic to update message counts
        refresh: () => {
          refresh();
          loadSubscriptions(topic);
        },
        type: "topic",
      });
      await loadSubscriptions(topic);
    } catch (error) {
      setErrorMessage("Error selecting topic or loading subscriptions, " + error.toString() );
      throw error;
    }
  };

  const onSubscriptionSelected = async (subscription) => {
    try {
      const { topic } = subscription;
      const { namespace } = topic;
      const [ , , , , resourceGroupName] = namespace.id.split("/");
      const rulesIterator = namespace.context().rules.listBySubscriptions(resourceGroupName, namespace.name, topic.name, subscription.name);
      const selectedSubscription = {
        namespaceName: namespace.name,
        namespaceId: namespace.id,
        namespaceUrl: namespace.serviceBusEndpoint,
        subscriptionId: namespace.subscriptionId,
        ...subscription,
        type: "subscription",
        topic,
      };
      onEntitySelected(selectedSubscription);
      const rules = [];
      for await (const rule of rulesIterator) {
        rules.push(rule);
      }
      // update selected entity (topic subscription) with rules, only if subscription is still selected in tree
      // in this case we just patch up rules to any existing info
      if(selectedNodeIdRef.current === selectedSubscription.id) {
        onEntityAdditionalInfoProvided({
          rules,
        });
      }
    } catch (error) {
      setErrorMessage("Error selecting topic subscription or loading filter rules, " + error.toString() );
      throw error;
    }
  }

  const onQueueSelected = (queue) => {
    try {
      const { namespace } = queue;
      onEntitySelected({
        namespaceName: namespace.name,
        namespaceId: namespace.id,
        namespaceUrl: namespace.serviceBusEndpoint,
        subscriptionId: namespace.subscriptionId,
        ...queue,
        type: "queue",
      });
    } catch (error) {
      setErrorMessage("Error selecting queue, " + error.toString() );
    }
  };


  const onEntityTreeItemLabelClick = (event) => {
    event.preventDefault();
  };


  const onTreeNodeSelected = (e, nodeId) => {
    setSelectedNodeId(nodeId);
  }

  return subscriptionsWithNamespaces && Object.keys(subscriptionsWithNamespaces).length > 0 ? (
    <Root>
      <FilterContainer container spacing={0} justifyContent="space-between" alignItems="flex-end">
        <Grid size={{ xs: 1 }}>
          <Box>
            <FilterList />
          </Box>
        </Grid>
        <Grid size={{ xs: 11 }} style={{
              width: "90%",
            }}>
          <TextField
            onChange={(e) => { setFilter(e.target.value); }} 
            value={filter}
            id="input-with-icon-grid" 
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <ClearFilterIcon onClick={() => { setFilter("") }}>
                    <Clear fontSize="small" color={filter.length > 0 ? "action" : "disabled"} />
                  </ClearFilterIcon>
                </InputAdornment>
              )
            }}
            label="Filter Entities" 
            variant="standard"
          />
        </Grid>
      </FilterContainer>
      
      <SimpleTreeView
  onSelectedItemsChange={(event, nodeId) => onTreeNodeSelected(nodeId)}
  multiSelect={false}
  slots={{
    collapseIcon: ArrowDropDown ,  
    expandIcon: ArrowRight ,        
    endIcon: () => <div style={{ width: 24 }} />, 
  }}
>
  {Object.values(subscriptionsWithNamespaces).sort(itemSort).map((item) => (
    <CustomTreeItem 
      labelIcon={AzureIcon} 
      labelText={item.name} 
      nodeId={item.id} 
      key={item.id}
      depth={1}
    >
      {Object.values(item.namespaces).sort(itemSort).map((namespace) => (
        <CustomTreeItem 
          labelIcon={NamespaceIcon} 
          labelText={namespace.tags.displayName ? namespace.tags.displayName : namespace.name} 
          nodeId={namespace.id}  
          key={namespace.id}
          depth={2}
          onClick={() => {onNamespaceSelected(namespace)}}
        >
          {namespace.isLoading || namespace.topics === undefined || namespace.queues === undefined ? 
            <CustomTreeItem  
              labelText="Loading..." 
              labelIcon={HourglassFull} 
              nodeId={namespace.id + "-loading"} 
              key={namespace.id + "-loading"}
              depth={3}
            /> : undefined}
          
          {namespace.topics && Object.keys(namespace.topics).length > 0 ? (
            <CustomTreeItem 
              labelIcon={Email} 
              labelText="Topics" 
              nodeId={namespace.id + "-topics"} 
              key={namespace.id + "-topics"}
              depth={3}
              labelIconStyle={{ color: blue[800] }}
            >
              {Object.values(namespace.topics).filter(entityNameFilter).sort(itemSort).map((topic) => (
                <CustomTreeItem 
                  labelIcon={Email}
                  labelText={`${topic.name}`}
                  labelTooltip={`${topic.name}`}
                  labelInfo={`${topic.countDetails.activeMessageCount}, ${topic.countDetails.deadLetterMessageCount}`}
                  nodeId={topic.id}
                  key={topic.id}
                  depth={4}
                  labelIconStyle={{ color: blue[800] }}
                  onLabelClick={onEntityTreeItemLabelClick}
                  onClick={() => {onTopicSelected(topic)}}
                >
                  {topic.subscriptions === undefined ? 
                    <CustomTreeItem  
                      labelText="Loading..." 
                      labelIcon={HourglassFull} 
                      nodeId={topic.id + "loading"} 
                      key={topic.id + "loading"}
                      depth={5}
                    /> : undefined}
                  {topic.subscriptions 
                    ? topic.subscriptions.sort(itemSort).map((subscription) => (
                        <CustomTreeItem 
                          labelIcon={Email}
                          labelText={`${subscription.name.length > 28 ? subscription.name.substr(0, 28) + ".." : subscription.name}`}
                          labelTooltip={`${subscription.name.length > 28 ? subscription.name : ""}`}
                          labelInfo={`${subscription.countDetails.activeMessageCount}, ${subscription.countDetails.deadLetterMessageCount}`}
                          nodeId={subscription.id}
                          key={subscription.id}
                          depth={5}
                          labelIconStyle={{ color: purple[300] }}
                          onLabelClick={onEntityTreeItemLabelClick}
                          onClick={() => {onSubscriptionSelected(subscription)}}
                        />
                      ))
                    : undefined
                  }
                </CustomTreeItem>
              ))}
            </CustomTreeItem>
          ) : undefined}

          {namespace.queues && Object.keys(namespace.queues).length > 0 ? (
            <CustomTreeItem 
              labelIcon={Email}
              labelText="Queues" 
              nodeId={namespace.id + "-queues"} 
              key={namespace.id + "-queues"}
              depth={3}
              labelIconStyle={{ color: deepPurple[800] }}
            >
              {Object.values(namespace.queues).filter(entityNameFilter).sort(itemSort).map((queue) => (
                <CustomTreeItem 
                  labelIcon={Email}
                  labelText={`${queue.name}`}
                  labelTooltip={`${queue.name}`}
                  labelInfo={`${queue.countDetails.activeMessageCount}, ${queue.countDetails.deadLetterMessageCount}`}
                  nodeId={queue.id}
                  key={queue.id}
                  depth={4}
                  labelIconStyle={{ color: deepPurple[800] }}
                  onLabelClick={onEntityTreeItemLabelClick}
                  onClick={() => {onQueueSelected(queue)}}
                />
              ))}
            </CustomTreeItem>
          ) : undefined}
        </CustomTreeItem>
      ))}
    </CustomTreeItem>
  ))}
</SimpleTreeView>
    </Root>
  )
  : <>
      {
        subscriptionsWithNamespaces === undefined ?
        <>
        <Spinner color="secondary" />
        <AlertContainer severity="info">
          Loading namespaces — ensure you have read access to Service Bus namespaces, then refresh page. 
          <br />
          <Link rel='noreferrer' href='https://github.com/cloudbricksio/ServiceBusCloudExplorerSupport/wiki/User-guide#authenticate-via-user-identity' target='_blank'>Read more here</Link>
        </AlertContainer>
        </>
        : null
      }
  </>;
}

EntityBrowser.propTypes = {
  credentials: PropTypes.object.isRequired,
  onEntitySelected: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  selectedEntity: PropTypes.object,
  onEntityAdditionalInfoProvided: PropTypes.func.isRequired,
};